import { COLLECTIONS_PAGE_PATH, INTERAC_PAYMENT_PAGE_LABEL } from '../../features/ledger/constants/interacPaymentPage';
import {
  PARTNER_COLLECTIONS_PAGE_PATH,
  PARTNER_COLLECTIONS_PAGE_LABEL,
} from '../../features/ledger/constants/partnerCollections';
import { hasPermission, canViewAllData } from '../../shared/utils/permissions';
import { isPathAllowedByPageAccess } from '../../shared/utils/pageAccessMatch';
import type { Permission } from '../../shared/utils/permissions';

export const TOP_BAR_HEIGHT = 60;

/** Temporarily off — unread-count API/socket flow has errors. */
export const SHOW_NAV_UNREAD_COUNTS = false;

export type NavIconKey =
  | 'dashboard'
  | 'assignment'
  | 'whatsapp'
  | 'accountBalance'
  | 'calendar'
  | 'meetingRoom'
  | 'headset'
  | 'trendingDown'
  | 'description'
  | 'article'
  | 'forum'
  | 'person'
  | 'settings'
  | 'tableChart'
  | 'smartToy'
  | 'currencyExchange'
  | 'accessTime'
  | 'trendingUp'
  | 'school'
  | 'policy'
  | 'gavel'
  | 'taskAlt'
  | 'notifications'
  | 'mail'
  | 'logout'
  | 'personOutline'
  | 'cameraAlt';

export interface NavItem {
  text: string;
  path: string;
  iconKey: NavIconKey;
  unreadCount?: number;
}

export interface AppNavGroups {
  primaryWork: NavItem[];
  moreWork: NavItem[];
  adminHubs: NavItem[];
  meMenu: NavItem[];
  showNotifications: boolean;
  /** Staff mailbox. Shown beside the notification bell, not in the work menu. */
  showMailbox: boolean;
}

export interface NavUser {
  permissionRole?: string;
  permissions?: Permission[];
  pageAccess?: string[];
}

export interface UnreadCounts {
  notifications: number;
  clientChats: number;
}

const PRIMARY_NAV_LIMIT = 8;

/** Preferred order for the work strip. First 8 the user can access stay visible; the rest go in More. */
const WORK_NAV_ORDER = [
  '/app',
  '/app/my-cases',
  '/app/my-chats',
  '/app/team-chat',
  '/app/my-tasks',
  '/app/walk-ins',
  '/app/appointments',
  '/app/my-documents',
  '/app/ai-assistant',
  '/app/forms',
  '/app/conversions',
  COLLECTIONS_PAGE_PATH,
  PARTNER_COLLECTIONS_PAGE_PATH,
  '/app/tasks',
  '/app/payouts',
  '/app/whatsapp-settings',
  '/app/chats-list',
];

const ME_MENU_PATHS = new Set([
  '/app/profile',
  '/app/my-attendance',
  '/app/my-training',
  '/app/my-policies',
  '/app/my-compliance',
]);

export function isNavItemActive(pathname: string, path: string): boolean {
  if (path === '/app/workspace') {
    const p = pathname;
    return (
      p === '/app/workspace' ||
      p.startsWith('/app/workspace/') ||
      p.startsWith('/app/office-management') ||
      p.startsWith('/app/manage-users') ||
      p.startsWith('/app/hr-profiles') ||
      p.startsWith('/app/hr-attendance') ||
      p.startsWith('/app/hr-training') ||
      p.startsWith('/app/hr-policy') ||
      p.startsWith('/app/departments') ||
      p.startsWith('/app/users/') ||
      p.startsWith('/app/workload')
    );
  }
  if (path === COLLECTIONS_PAGE_PATH) {
    return (
      pathname === path ||
      pathname.startsWith(`${path}/`) ||
      pathname === '/app/ledger/interac-payment-claims' ||
      pathname.startsWith('/app/ledger/interac-payment-claims/')
    );
  }
  if (path === '/app/chats-list') {
    const p = pathname;
    return (
      p === '/app/chats-list' ||
      p.startsWith('/app/chats-list/') ||
      p === '/app/chats-dashboard' ||
      p.startsWith('/app/chats-dashboard/') ||
      p === '/app/chat-topics' ||
      p.startsWith('/app/chat-topics/') ||
      p === '/app/all-community' ||
      p.startsWith('/app/all-community/')
    );
  }
  if (path === '/app/my-chats') {
    const p = pathname;
    return (
      p === '/app/my-chats' ||
      p.startsWith('/app/my-chats/') ||
      p === '/app/my-calls' ||
      p.startsWith('/app/my-calls/') ||
      p === '/app/my-chat-assignments' ||
      p.startsWith('/app/my-chat-assignments/') ||
      p === '/app/my-community' ||
      p.startsWith('/app/my-community/')
    );
  }
  if (path === '/app/orders-dashboard') {
    const p = pathname;
    return (
      p === '/app/orders-dashboard' ||
      p.startsWith('/app/orders-dashboard/') ||
      p.startsWith('/app/service-orders') ||
      p.startsWith('/app/so/') ||
      p.startsWith('/app/manage-services') ||
      p.startsWith('/app/chat-topics') ||
      p.startsWith('/app/clients') ||
      p.startsWith('/app/forms-management') ||
      p.startsWith('/app/whatsapp-settings') ||
      p.startsWith('/app/reports') ||
      p.startsWith('/app/legal-tasks') ||
      p.startsWith('/app/shipping')
    );
  }
  if (path === '/app/photo-jobs') {
    return pathname === path || pathname.startsWith(`${path}/`);
  }
  if (
    path === '/app/ledger' ||
    path === '/app/documents' ||
    path === '/app/my-cases' ||
    path === '/app/reports' ||
    path === '/app/sheets' ||
    path === '/app/ai-assistant' ||
    path === '/app/admin-assistant' ||
    path === '/app/team-chat' ||
    path === '/app/my-attendance' ||
    path === '/app/my-training' ||
    path === '/app/my-policies' ||
    path === '/app/my-compliance' ||
    path === '/app/profile' ||
    path === '/app/mailbox'
  ) {
    return pathname === path || pathname.startsWith(`${path}/`);
  }
  if (path === '/app/contact-center') {
    return pathname === path || pathname.startsWith(`${path}/`);
  }
  if (path === '/app') {
    return pathname === '/app' || pathname === '/app/';
  }
  return pathname === path;
}

function buildAdminPages(unreadCounts: UnreadCounts): NavItem[] {
  return [
    {
      text: 'Chats Dashboard',
      iconKey: 'whatsapp',
      path: '/app/chats-list',
      ...(SHOW_NAV_UNREAD_COUNTS ? { unreadCount: unreadCounts.clientChats } : {}),
    },
    { text: 'Ops Dashboard', iconKey: 'assignment', path: '/app/orders-dashboard' },
    { text: 'Contact Center', iconKey: 'headset', path: '/app/contact-center' },
    { text: 'Document Library', iconKey: 'description', path: '/app/documents' },
    { text: 'Workspace', iconKey: 'settings', path: '/app/workspace' },
    { text: 'Ledger', iconKey: 'accountBalance', path: '/app/ledger' },
    { text: 'RCIC Photo Workflows', iconKey: 'cameraAlt', path: '/app/photo-jobs' },
  ];
}

function buildMemberPages(
  user: NavUser,
  unreadCounts: UnreadCounts,
  hasPaymentAccess: boolean,
  canViewAllChats: boolean
): NavItem[] {
  return [
    { text: 'Home', iconKey: 'dashboard', path: '/app' },
    { text: 'My Cases', iconKey: 'personOutline', path: '/app/my-cases' },
    { text: 'My Sheets', iconKey: 'tableChart', path: '/app/my-documents' },
    ...(canViewAllChats && user.permissionRole !== 'admin'
      ? [
          {
            text: 'Chats Dashboard',
            iconKey: 'whatsapp' as const,
            path: '/app/chats-list',
            ...(SHOW_NAV_UNREAD_COUNTS ? { unreadCount: unreadCounts.clientChats } : {}),
          },
        ]
      : []),
    {
      text: 'My chats',
      iconKey: 'whatsapp',
      path: '/app/my-chats',
      ...(SHOW_NAV_UNREAD_COUNTS ? { unreadCount: unreadCounts.clientChats } : {}),
    },
    { text: 'Mailbox', iconKey: 'mail', path: '/app/mailbox' },
    { text: 'Team Chat', iconKey: 'forum', path: '/app/team-chat' },
    { text: 'AI Assistant', iconKey: 'smartToy', path: '/app/ai-assistant' },
    {
      text: 'Notifications',
      iconKey: 'notifications',
      path: '/app/notifications',
      ...(SHOW_NAV_UNREAD_COUNTS ? { unreadCount: unreadCounts.notifications } : {}),
    },
    { text: 'My Tasks', iconKey: 'assignment', path: '/app/my-tasks' },
    { text: 'My Attendance', iconKey: 'accessTime', path: '/app/my-attendance' },
    { text: 'My Training', iconKey: 'school', path: '/app/my-training' },
    { text: 'My Policies', iconKey: 'policy', path: '/app/my-policies' },
    { text: 'My Compliance', iconKey: 'gavel', path: '/app/my-compliance' },
    ...(hasPaymentAccess ||
    isPathAllowedByPageAccess(user.pageAccess || [], '/app/payouts') ||
    isPathAllowedByPageAccess(user.pageAccess || [], '/app/outflow')
      ? [{ text: 'Payouts', iconKey: 'trendingDown' as const, path: '/app/payouts' }]
      : []),
    { text: 'Walk-In', iconKey: 'meetingRoom', path: '/app/walk-ins' },
    { text: 'Conversions', iconKey: 'trendingUp', path: '/app/conversions' },
    { text: INTERAC_PAYMENT_PAGE_LABEL, iconKey: 'currencyExchange', path: COLLECTIONS_PAGE_PATH },
    { text: PARTNER_COLLECTIONS_PAGE_LABEL, iconKey: 'currencyExchange', path: PARTNER_COLLECTIONS_PAGE_PATH },
    { text: 'Forms', iconKey: 'article', path: '/app/forms' },
    { text: 'Tasks', iconKey: 'taskAlt', path: '/app/tasks' },
    { text: 'Appointment', iconKey: 'calendar', path: '/app/appointments' },
    { text: 'Quick Message', iconKey: 'whatsapp', path: '/app/whatsapp-settings' },
  ];
}

function filterPagesForUser(
  user: NavUser,
  adminPages: NavItem[],
  memberPages: NavItem[],
  canViewAllChats: boolean
): { adminPages: NavItem[]; memberPages: NavItem[] } {
  const role = user.permissionRole?.toLowerCase().replace(/\s+/g, '_');

  if (role === 'admin') {
    const pa = user.pageAccess || [];
    return {
      adminPages: adminPages.filter((item) => isPathAllowedByPageAccess(pa, item.path)),
      memberPages: memberPages.filter(
        (item) =>
          item.path === '/app/my-documents' ||
          item.path === '/app/admin-assistant' ||
          isPathAllowedByPageAccess(pa, item.path)
      ),
    };
  }

  if (role === 'operations_manager') {
    return { adminPages, memberPages };
  }

  const pageAccess = user.pageAccess || [];
  const hasOrdersHubAccess =
    pageAccess.includes('/app/orders-dashboard') ||
    pageAccess.includes('/app/service-orders') ||
    pageAccess.includes('/app/orders-dashboard/my-cases') ||
    pageAccess.includes('/app/my-cases');

  const filteredMemberPages = memberPages.filter((item) => {
    if (item.path === '/app') return true;
    if (item.path === '/app/sheets') return true;
    if (item.path === '/app/ai-assistant') {
      return pageAccess.includes('/app/ai-assistant');
    }
    if (item.path === '/app/admin-assistant') {
      return true;
    }
    if (item.path === '/app/my-cases') return hasOrdersHubAccess;
    if (item.path === '/app/chats-list') {
      return (
        canViewAllChats &&
        user.permissionRole !== 'admin' &&
        (pageAccess.includes('/app/chats-list') ||
          pageAccess.includes('/app/client-chats/list') ||
          pageAccess.length === 0)
      );
    }
    if (item.path === '/app/my-chats') {
      return (
        pageAccess.includes('/app/my-chats') ||
        pageAccess.includes('/app/client-chats/my-chats') ||
        pageAccess.includes('/app/client-chats')
      );
    }
    return pageAccess.includes(item.path);
  });

  const filteredAdminPages = adminPages.filter((item) =>
    isPathAllowedByPageAccess(pageAccess, item.path)
  );

  return { adminPages: filteredAdminPages, memberPages: filteredMemberPages };
}

function splitWorkNav(items: NavItem[]): { primaryWork: NavItem[]; moreWork: NavItem[] } {
  const rank = new Map(WORK_NAV_ORDER.map((path, index) => [path, index]));
  const ordered = [...items].sort((a, b) => {
    const aRank = rank.get(a.path) ?? WORK_NAV_ORDER.length;
    const bRank = rank.get(b.path) ?? WORK_NAV_ORDER.length;
    return aRank - bRank;
  });
  return {
    primaryWork: ordered.slice(0, PRIMARY_NAV_LIMIT),
    moreWork: ordered.slice(PRIMARY_NAV_LIMIT),
  };
}

function canAccessMePath(user: NavUser, path: string): boolean {
  if (path === '/app/profile') return true;
  const role = user.permissionRole?.toLowerCase().replace(/\s+/g, '_');
  if (role === 'operations_manager') return true;
  const pa = user.pageAccess || [];
  if (role === 'admin') {
    return isPathAllowedByPageAccess(pa, path);
  }
  return pa.includes(path);
}

/**
 * Build top-bar nav groups from the same pageAccess / role rules as the former sidebar.
 */
export function buildAppNav(
  user: NavUser | null | undefined,
  unreadCounts: UnreadCounts = { notifications: 0, clientChats: 0 }
): AppNavGroups {
  if (!user) {
    return {
      primaryWork: [],
      moreWork: [],
      adminHubs: [],
      meMenu: [{ text: 'My Profile', iconKey: 'person', path: '/app/profile' }],
      showNotifications: false,
      showMailbox: false,
    };
  }

  const userPermissions = user.permissions || [];
  const hasPaymentAccess = hasPermission(userPermissions, 'payments.read');
  const canViewAllChats = canViewAllData(userPermissions, user.permissionRole);

  const rawAdmin = buildAdminPages(unreadCounts);
  const rawMember = buildMemberPages(user, unreadCounts, hasPaymentAccess, canViewAllChats);
  const { adminPages, memberPages } = filterPagesForUser(user, rawAdmin, rawMember, canViewAllChats);

  const notificationsItem = memberPages.find((item) => item.path === '/app/notifications');
  const mailboxItem = memberPages.find((item) => item.path === '/app/mailbox');
  const workItems = memberPages.filter(
    (item) =>
      item.path !== '/app/notifications' &&
      item.path !== '/app/mailbox' &&
      !ME_MENU_PATHS.has(item.path)
  );
  const { primaryWork, moreWork } = splitWorkNav(workItems);

  const meMenu: NavItem[] = [
    { text: 'My Profile', iconKey: 'person', path: '/app/profile' },
    ...[
      { text: 'My Attendance', iconKey: 'accessTime' as const, path: '/app/my-attendance' },
      { text: 'My Training', iconKey: 'school' as const, path: '/app/my-training' },
      { text: 'My Policies', iconKey: 'policy' as const, path: '/app/my-policies' },
      { text: 'My Compliance', iconKey: 'gavel' as const, path: '/app/my-compliance' },
    ].filter((item) => canAccessMePath(user, item.path)),
  ];

  return {
    primaryWork,
    moreWork,
    adminHubs: adminPages,
    meMenu,
    showNotifications: Boolean(notificationsItem) || canAccessMePath(user, '/app/notifications'),
    // Workspace shows Mailbox to every admin. The bell icon uses the same rule.
    showMailbox:
      Boolean(mailboxItem) ||
      user.permissionRole?.toLowerCase().replace(/\s+/g, '_') === 'admin',
  };
}
